// components/Tabs.tsx
import React from 'react';

interface TabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  tabs: { key: string; icon?: React.ReactNode; label?: string }[]; // `icon` and `label` are optional; an icon tab's label is its tooltip
  fullWidth?: boolean; // Boolean to control maximum width and equal button sizes
  tabButtonMaxWidth?: string; // New prop: Max width for each individual tab button (e.g., '100px', '8rem')
  showBorderBottom?: boolean; // New prop to control the bottom border visibility
}

const BORDER_STYLE: React.CSSProperties = { borderColor: '#444' };
const EMPTY_STYLE: React.CSSProperties = {};

interface TabButtonProps {
  tabKey: string;
  icon?: React.ReactNode;
  label: string;
  /** Tooltip of an icon tab: only an explicit label, never the bare key. */
  hint?: string;
  isActive: boolean;
  fullWidth: boolean;
  maxWidth?: string;
  onTabChange: (tab: string) => void;
}

const TabButtonComponent: React.FC<TabButtonProps> = ({
  tabKey,
  icon,
  label,
  hint,
  isActive,
  fullWidth,
  maxWidth,
  onTabChange,
}) => {
  const handleClick = React.useCallback(() => {
    onTabChange(tabKey);
  }, [onTabChange, tabKey]);

  const buttonStyle = React.useMemo<React.CSSProperties>(
    () =>
      maxWidth
        ? {
            maxWidth,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }
        : EMPTY_STYLE,
    [maxWidth]
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`cursor-pointer relative z-10 flex items-center justify-center ${ // Added relative z-10 to buttons
        fullWidth ? 'flex-1' : '' // flex-1 still allows buttons to grow, but maxWidth will cap them
      } h-[22px] rounded-[8px] transition-colors youssef ${
        isActive ? 'blue--selected' : ''
      }`}
      style={buttonStyle} // Apply the dynamic style here
      title={icon ? hint : label} // Add title for potential truncation
      aria-label={label}
      aria-pressed={isActive}
    >
      {icon ? (
        <span className={isActive ? 'text-[#ECECEC]' : 'text-[#B4B4B4]'}>
          {icon}
        </span>
      ) : (
        <span className="text-xs font-medium capitalize">{label}</span>
      )}
    </button>
  );
};

const TabButton = React.memo(TabButtonComponent);
TabButton.displayName = 'TabButton';

const TabsComponent: React.FC<TabsProps> = ({
  activeTab,
  onTabChange,
  tabs,
  fullWidth = false,
  tabButtonMaxWidth, // Destructure the new prop
  showBorderBottom = true, // Default value set to true here
}) => {
  return (
    <div className={`relative ${fullWidth ? 'w-full' : 'inline-flex'}`}>
      {/* Conditionally render the border based on showBorderBottom */}
      {showBorderBottom && (
        <div
          className="absolute bottom-0 left-0 w-full border-b"
          style={BORDER_STYLE}
        ></div>
      )}

      <div
        className={`flex ${
          fullWidth ? 'w-full justify-center' : 'justify-start'
        } gap-6 px-4 py-2`}
      >
        {tabs.map(({ key, icon, label }, index) => (
          <TabButton
            key={key}
            tabKey={key}
            icon={icon}
            // Use label if provided, otherwise key, otherwise index
            label={label || key || String(index)}
            hint={label}
            isActive={activeTab === key}
            fullWidth={fullWidth}
            maxWidth={tabButtonMaxWidth}
            onTabChange={onTabChange}
          />
        ))}
      </div>
    </div>
  );
};

const Tabs = React.memo(TabsComponent);

Tabs.displayName = 'Tabs';

export default Tabs;
